import { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { fetchSchools } from "@/services/schools";
import type { SchoolResult } from "@/services/schools";

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;
const MIN_QUERY_LENGTH = 2;

export interface SchoolTypeaheadProps {
  /** Called with the raw search result when the user picks a school. */
  onSelect: (school: SchoolResult) => void;
  /** Team ids to hide from results (already-picked schools). */
  excludeTeamIds?: string[];
  placeholder?: string;
  /** Player's program ("male"/"female") — the search endpoint requires it. */
  gender?: string | null;
}

/**
 * Debounced school name typeahead over `fetchSchools` (the `q` text-search
 * param). Shared by the dream-schools and recruiting-progress onboarding steps.
 */
export default function SchoolTypeahead({
  onSelect,
  excludeTeamIds,
  placeholder = "Search schools…",
  gender,
}: SchoolTypeaheadProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SchoolResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Monotonic id so stale responses (or responses after a pick) are ignored.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      requestIdRef.current++;
      setResults([]);
      setLoading(false);
      setError(false);
      setHighlightedIndex(-1);
      return;
    }
    const id = ++requestIdRef.current;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(false);
      fetchSchools({ q, limit: MAX_RESULTS, gender: gender ?? undefined })
        .then(resp => {
          if (requestIdRef.current === id) setResults(resp.results);
        })
        .catch(() => {
          if (requestIdRef.current === id) {
            setResults([]);
            setError(true);
          }
        })
        .finally(() => {
          if (requestIdRef.current === id) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, gender]);

  const visible = excludeTeamIds?.length
    ? results.filter(r => !excludeTeamIds.includes(r.schoolId))
    : results;

  const handleSelect = (school: SchoolResult) => {
    requestIdRef.current++;
    setQuery("");
    setResults([]);
    setLoading(false);
    setHighlightedIndex(-1);
    onSelect(school);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-14 rounded-2xl border-2 border-border-strong bg-white pl-11 pr-11 text-sm text-forest shadow-[0_3px_0_#d7e2d9] placeholder:text-subtle focus-visible:border-primary-text focus-visible:ring-primary"
          placeholder={placeholder}
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setHighlightedIndex(-1);
          }}
          onKeyDown={event => {
            if (event.key === "ArrowDown" && visible.length > 0) {
              event.preventDefault();
              setHighlightedIndex(current => (current + 1) % visible.length);
            } else if (event.key === "ArrowUp" && visible.length > 0) {
              event.preventDefault();
              setHighlightedIndex(current => (current <= 0 ? visible.length - 1 : current - 1));
            } else if (event.key === "Enter" && highlightedIndex >= 0) {
              event.preventDefault();
              handleSelect(visible[highlightedIndex]);
            } else if (event.key === "Escape") {
              setResults([]);
              setHighlightedIndex(-1);
            }
          }}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={visible.length > 0}
          aria-controls="school-search-results"
          aria-activedescendant={
            highlightedIndex >= 0
              ? `school-result-${visible[highlightedIndex]?.schoolId}`
              : undefined
          }
        />
        {loading && (
          <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {visible.length > 0 && (
        <ul
          id="school-search-results"
          role="listbox"
          className="absolute z-50 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-border bg-white p-1.5 shadow-xl"
        >
          {visible.map((s, index) => (
            <li key={s.schoolId}>
              <button
                id={`school-result-${s.schoolId}`}
                type="button"
                role="option"
                aria-selected={highlightedIndex === index}
                className={
                  "w-full rounded-xl px-3 py-2.5 text-left text-sm transition-colors " +
                  (highlightedIndex === index ? "bg-lime-soft" : "hover:bg-lime-soft")
                }
                onClick={() => handleSelect(s)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="font-bold text-forest">{s.schoolName}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {[s.division, s.state].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="mt-2 text-xs text-destructive">
          Search failed — please try again in a moment.
        </p>
      )}
    </div>
  );
}
